import React, { useState, useEffect } from 'react';

interface LinkConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  url: string;
  isDarkMode?: boolean;
}

export const LinkConfirmationModal: React.FC<LinkConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  url,
  isDarkMode = false
}) => {
  const [favicon, setFavicon] = useState<string>('');

  // Extract domain from URL for display
  const getDomain = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  // Get favicon URL
  const getFaviconUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`;
    } catch {
      return '';
    }
  };

  useEffect(() => {
    if (isOpen && url) {
      setFavicon(getFaviconUrl(url));
    }
  }, [isOpen, url]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter') onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center" onClick={handleBackdropClick} onKeyDown={handleKeyDown} tabIndex={-1}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className={`relative z-[81] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${
          isDarkMode ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-modal-title"
        aria-describedby="link-modal-description"
      >
        <h3 id="link-modal-title" className="text-lg font-semibold mb-2">Open external link?</h3>
        <div className="flex flex-col gap-2 my-3">
          {favicon ? (
            <img
              src={favicon}
              alt="Site icon"
              className="w-5 h-5 rounded mr-auto"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className={`w-5 h-5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
          )}
          <span className="text-sm text-left truncate font-medium">{url}</span>
        </div>
        <div className="flex justify-end gap-2">
          <button
            className={`px-3 py-2 rounded-full ${isDarkMode ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' : 'bg-transparent hover:bg-gray-100 text-gray-700'}`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 rounded-full ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            onClick={onConfirm}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};
